import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { Card, CardBody, CardHeader, Container, Row, Col, Label, Input, Button, Spinner } from 'reactstrap';
import { useSelector, useDispatch } from 'react-redux';
import { getFaqDetail, updateFaqDetail } from '../../slices/faq/thunk';
import { selectFaqEditData } from '../../slices/faq/selectors';
import { CKEditor } from '@ckeditor/ckeditor5-react';
import ClassicEditor from '@ckeditor/ckeditor5-build-classic';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

const FAQEdit = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const [localFaq, setLocalFaq] = useState(null);

    const { currentFaq, loading, error } = useSelector(selectFaqEditData);
    console.log("Redux State:", { currentFaq, loading, error });

    useEffect(() => {
        if (id) {
            console.log("Dispatching getFaqDetail with ID:", id);
            dispatch(getFaqDetail(id));
        }
    }, [dispatch, id]);

    useEffect(() => {
        if (currentFaq) {
            setLocalFaq(currentFaq);
        }
    }, [currentFaq]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        try {
            const response = await APIClient.update(`/python/faq/${id}/`, formData);
            if (response.status === 200) {
                toast.success('FAQ updated successfully', {
                    position: "top-right",
                    autoClose: 5000,
                    hideProgressBar: false,
                    closeOnClick: true,
                    pauseOnHover: true,
                    draggable: true,
                    theme: "colored"
                });
                navigate('/faq');
            }
        } catch (error) {
            toast.error(error.message || 'Failed to update FAQ', {
                position: "top-right",
                autoClose: 5000,
                hideProgressBar: false,
                closeOnClick: true,
                pauseOnHover: true,
                draggable: true,
                theme: "colored"
            });
        }
    };

    if (loading) {
        console.log("Loading state active");
        return (
            <div className="d-flex justify-content-center align-items-center" style={{ height: '400px' }}>
                <Spinner color="primary" />
            </div>
        );
    }

    if (error) {
        console.log("Error state active:", error);
        return (
            <div className="alert alert-danger" role="alert">
                <h4 className="alert-heading">Error Loading FAQ</h4>
                <p>{error.message}</p>
                <hr />
                <p className="mb-0">
                    <Button 
                        color="link" 
                        className="p-0" 
                        onClick={() => navigate('/faq-settings')}
                    >
                        Return to FAQ List
                    </Button>
                </p>
            </div>
        );
    }

    if (!localFaq) {
        console.log("Local FAQ state:", localFaq);
        return (
            <div className="d-flex justify-content-center align-items-center" style={{ height: '400px' }}>
                <Spinner color="primary" />
            </div>
        );
    }

    return (
        <React.Fragment>
            <div className="page-content" style={{ minHeight: '100vh', padding: '20px' }}>
                <Container fluid>
                    <div className="mb-3">
                        <Button
                            color="soft-primary"
                            onClick={() => navigate('/faq-settings')}
                            className="btn-label"
                        >
                            <i className="ri-arrow-left-s-line label-icon align-middle fs-16 me-2"></i>
                            Back to FAQ List
                        </Button>
                    </div>
                    <Card>
                        <CardHeader className="bg-light">
                            <h4 className="card-title mb-0">
                                <Link to="/faq-settings" className="text-primary">FAQ</Link>
                                <span className="text-muted mx-2">&gt;</span>
                                Edit FAQ
                            </h4>
                        </CardHeader>
                        <CardBody>
                            {localFaq && (
                                <Row>
                                    <Col xs={12}>
                                        <div className="mb-3">
                                            <Label className="form-label">Title</Label>
                                            <Input
                                                type="text"
                                                className="form-control"
                                                value={localFaq.title?.en || ''}
                                                onChange={(e) => setLocalFaq(prev => ({
                                                    ...prev,
                                                    title: { ...prev.title, en: e.target.value }
                                                }))}
                                            />
                                        </div>
                                    </Col>
                                    <Col xs={12}>
                                        <div className="mb-3">
                                            <div className="form-check">
                                                <Input
                                                    type="checkbox"
                                                    className="form-check-input"
                                                    id="hasSubPoint"
                                                    checked={localFaq.hasSubPoint}
                                                    onChange={(e) => setLocalFaq(prev => ({
                                                        ...prev,
                                                        hasSubPoint: e.target.checked,
                                                        htmlContent: e.target.checked ? { en: '' } : prev.htmlContent
                                                    }))}
                                                />
                                                <Label className="form-check-label" for="hasSubPoint">
                                                    Has Sub Category
                                                </Label>
                                            </div>
                                        </div>
                                    </Col>
                                    {!localFaq.hasSubPoint && (
                                        <Col xs={12}>
                                            <div className="mb-3">
                                                <Label className="form-label">Content</Label>
                                                <div className="ckeditor-classic">
                                                    {localFaq.htmlContent?.en !== undefined && (
                                                        <CKEditor
                                                            editor={ClassicEditor}
                                                            data={localFaq.htmlContent?.en || ''}
                                                            onChange={(event, editor) => {
                                                                const data = editor.getData();
                                                                setLocalFaq(prev => ({
                                                                    ...prev,
                                                                    htmlContent: { ...prev.htmlContent, en: data }
                                                                }));
                                                            }}
                                                        />
                                                    )}
                                                </div>
                                            </div>
                                        </Col>
                                    )}
                                    <Col xs={12} className="mt-4">
                                        <div className="text-end">
                                            <Button 
                                                type="button" 
                                                color="light" 
                                                className="me-2"
                                                onClick={() => navigate('/faq-settings')}
                                            >
                                                Cancel
                                            </Button>
                                            <Button 
                                                type="button" 
                                                color="primary"
                                                onClick={() => {
                                                    dispatch(updateFaqDetail(id, localFaq));
                                                    navigate('/faq-settings');
                                                }}
                                            >
                                                Update FAQ
                                            </Button>
                                        </div>
                                    </Col>
                                </Row>
                            )}
                        </CardBody>
                    </Card>
                </Container>
            </div>
            <ToastContainer
                position="top-right"
                autoClose={5000}
                hideProgressBar={false}
                newestOnTop={false}
                closeOnClick
                rtl={false}
                pauseOnFocusLoss
                draggable
                pauseOnHover
                theme="colored"
            />
        </React.Fragment>
    );
};

export default FAQEdit; 